/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';

import {
  pathRequiresSelectedCompanyOnRequest,
  resolveCompanyScopeIdForRequest,
  withSelectedCompanyScopeFetchInit,
  readExplicitCompanyScopeHeader,
  attachSelectedCompanyIdToRequest,
} from '../config/company-scope.config';
import {
  assertCompanyScopeCurrent, combineCompanyScopeSignal, getCompanyScopeSnapshot,
  guardCompanyScopeResponse, registerCompanyScopeInvalidator,
  isWorkspaceBoundRequest, assertWorkspaceRequestAllowed,
} from '../utils/company-scope-lifecycle.utils';
import { WORKSPACE_CONTEXT_CHANGED_EVENT } from '../context/workspace-transition.utils';

function attachWorkspaceFence(config: AxiosRequestConfig, snapshot: ReturnType<typeof getCompanyScopeSnapshot>): void {
  if (!snapshot.membershipId) return;
  const headers = config.headers as any;
  if (typeof headers?.set === 'function') headers.set('X-Workspace-Membership', snapshot.membershipId, false);
  else if (!Object.keys(headers ?? {}).some(key => key.toLowerCase() === 'x-workspace-membership')) {
    config.headers = { ...config.headers, 'X-Workspace-Membership': snapshot.membershipId };
  }
}
function reportWorkspaceMismatch(error: any): void {
  if (typeof window !== 'undefined' && error?.response?.status === 409
    && (error.response.data?.code === 'WORKSPACE_CONTEXT_CHANGED' || error.response.data?.details?.code === 'WORKSPACE_CONTEXT_CHANGED')) {
    window.dispatchEvent(new Event(WORKSPACE_CONTEXT_CHANGED_EVENT));
  }
}

const AXIOS_GET_COALESCE_PATCHED = Symbol.for('qubepro.axios.get.coalesce');
const AXIOS_REQUEST_COALESCE_PATCHED = Symbol.for('qubepro.axios.request.coalesce');
const AXIOS_ADAPTER_COALESCE_PATCHED = Symbol.for('qubepro.axios.adapter.coalesce');
const FETCH_GET_COALESCE_PATCHED = Symbol.for('qubepro.fetch.get.coalesce');
const automaticCompanySignals = new WeakSet<AbortSignal>();

/**
 * Dynamic List Page Kit için axios erişim noktası.
 *
 * Not: Bu projede `AuthContext` global axios interceptor'larını ve
 * `withCredentials` davranışını zaten yönetiyor. Bu dosya yeni bir instance
 * oluşturmadan global axios'u export eder.
 */
axios.defaults.withCredentials = true;

/** Dev: aynı Vite portuna kaç ayrı sekme/cihaz bağlı — proxy sayaçlarını yorumlamak için (sessionStorage / sekme başına tek id). */
const DEV_TAB_HEADER = 'X-QubePro-Dev-Browser-Tab';
const DEV_ROUTE_HEADER = 'X-QubePro-Dev-Route';
const DEV_VISIBILITY_HEADER = 'X-QubePro-Dev-Visibility';
const DEV_BUNDLE_HEADER = 'X-QubePro-Dev-Bundle';
const DEV_LOAD_HEADER = 'X-QubePro-Dev-Load';
const DEV_NAVIGATION_HEADER = 'X-QubePro-Dev-Navigation';
const DEV_RELOAD_REASON_HEADER = 'X-QubePro-Dev-Reload-Reason';
const DEV_BUNDLE_ID = 'request-meta-v2';
const DEV_TAB_STORAGE_KEY = 'qubepro_dev_browser_tab';
const DEV_DOCUMENT_ID_GLOBAL_KEY = 'qubepro_dev_document_id';
const DEV_RELOAD_REASON_STORAGE_KEY = 'qubepro_dev_reload_reason';

function createDevRequestId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function getDevDocumentId(): string {
  const g = globalThis as typeof globalThis & Record<string, string | undefined>;
  if (!g[DEV_DOCUMENT_ID_GLOBAL_KEY]) {
    g[DEV_DOCUMENT_ID_GLOBAL_KEY] = createDevRequestId('ld');
  }
  return g[DEV_DOCUMENT_ID_GLOBAL_KEY]!;
}

function getDevNavigationType(): string {
  try {
    const entries = performance.getEntriesByType?.('navigation') || [];
    const nav = entries[0] as PerformanceNavigationTiming | undefined;
    return (nav?.type || 'unknown').slice(0, 24);
  } catch {
    return 'unknown';
  }
}

function rememberDevReloadReason(reason: string): void {
  try {
    sessionStorage.setItem(
      DEV_RELOAD_REASON_STORAGE_KEY,
      JSON.stringify({
        at: Date.now(),
        reason,
        path: `${window.location.pathname || '/'}${window.location.search || ''}`,
      }),
    );
  } catch {
    /* ignore */
  }
}

function getDevReloadReason(): string {
  try {
    const raw = sessionStorage.getItem(DEV_RELOAD_REASON_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { at?: number; reason?: string; path?: string };
      if (typeof parsed.at === 'number' && Date.now() - parsed.at < 30000 && parsed.reason) {
        sessionStorage.removeItem(DEV_RELOAD_REASON_STORAGE_KEY);
        return String(parsed.reason).slice(0, 80);
      }
    }
  } catch {
    /* ignore */
  }
  return '';
}

if (import.meta.env.DEV && import.meta.hot) {
  import.meta.hot.on('vite:beforeFullReload', (payload: unknown) => {
    const path =
      payload && typeof payload === 'object' && 'path' in payload
        ? String((payload as { path?: unknown }).path || '')
        : '';
    rememberDevReloadReason(path ? `vite-full-reload:${path}` : 'vite-full-reload');
  });
}

function getDevRequestMetaHeaders(): Record<string, string> {
  if (!import.meta.env.DEV || typeof window === 'undefined' || typeof document === 'undefined') return {};
  try {
    let tabId = sessionStorage.getItem(DEV_TAB_STORAGE_KEY);
    if (!tabId) {
      tabId = createDevRequestId('bt');
      sessionStorage.setItem(DEV_TAB_STORAGE_KEY, tabId);
    }
    const documentId = getDevDocumentId();
    const navigationType = getDevNavigationType();
    return {
      // sessionStorage duplicate tab ile kopyalanabilir; documentId ayni oturum kopyalarini ayirir.
      [DEV_TAB_HEADER]: `${tabId}/${documentId}`,
      [DEV_ROUTE_HEADER]: `${window.location.pathname || '/'}${window.location.search || ''}`.slice(0, 180),
      [DEV_VISIBILITY_HEADER]: document.visibilityState || 'unknown',
      [DEV_BUNDLE_HEADER]: DEV_BUNDLE_ID,
      [DEV_LOAD_HEADER]: documentId,
      [DEV_NAVIGATION_HEADER]: navigationType,
      [DEV_RELOAD_REASON_HEADER]: getDevReloadReason(),
    };
  } catch {
    return {};
  }
}

function withDevFetchMetaHeaders(
  input: RequestInfo | URL,
  init?: RequestInit,
): [RequestInfo | URL, RequestInit | undefined] {
  const meta = getDevRequestMetaHeaders();
  if (Object.keys(meta).length === 0) return [input, init];

  const baseHeaders =
    init?.headers ??
    (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined);
  const headers = new Headers(baseHeaders);
  for (const [key, value] of Object.entries(meta)) {
    headers.set(key, value);
  }
  return [input, { ...(init || {}), headers }];
}

if (import.meta.env.DEV) {
  axios.interceptors.request.use((config) => {
    try {
      const meta = getDevRequestMetaHeaders();
      const h = config.headers;
      if (h && typeof (h as { set?: (a: string, b: string) => void }).set === 'function') {
        for (const [key, value] of Object.entries(meta)) {
          (h as { set: (a: string, b: string) => void }).set(key, value);
        }
      } else if (h && typeof h === 'object' && !Array.isArray(h)) {
        Object.assign(h as Record<string, string>, meta);
      }
    } catch {
      /* sessionStorage yoksa atla */
    }
    return config;
  });
}

type CoalesceMaps = {
  generation: number;
  notificationGeneration?: number;
  inflight: Map<string, Promise<AxiosResponse>>;
  recentOk: Map<string, { at: number; response: AxiosResponse }>;
  recentError: Map<string, { at: number; error: unknown }>;
};

type AxiosAdapter = (config: AxiosRequestConfig) => Promise<AxiosResponse>;

type FetchCoalesceMaps = {
  generation: number;
  notificationGeneration?: number;
  inflight: Map<string, Promise<Response>>;
  recentOk: Map<string, { at: number; response: Response }>;
  recentError: Map<string, { at: number; error: unknown }>;
};

const COALESCE_GLOBAL_KEY = 'qubepro_axios_get_coalesce';
const FETCH_COALESCE_GLOBAL_KEY = 'qubepro_fetch_get_coalesce';

function getCoalesceMaps(): CoalesceMaps {
  const g = globalThis as typeof globalThis & { [COALESCE_GLOBAL_KEY]?: CoalesceMaps };
  if (!g[COALESCE_GLOBAL_KEY]) {
    g[COALESCE_GLOBAL_KEY] = {
      generation: 0,
      inflight: new Map(),
      recentOk: new Map(),
      recentError: new Map(),
    };
  } else if (!g[COALESCE_GLOBAL_KEY].recentError) {
    g[COALESCE_GLOBAL_KEY].recentError = new Map();
  }
  g[COALESCE_GLOBAL_KEY].generation ??= 0;
  return g[COALESCE_GLOBAL_KEY]!;
}

function getFetchCoalesceMaps(): FetchCoalesceMaps {
  const g = globalThis as typeof globalThis & { [FETCH_COALESCE_GLOBAL_KEY]?: FetchCoalesceMaps };
  if (!g[FETCH_COALESCE_GLOBAL_KEY]) {
    g[FETCH_COALESCE_GLOBAL_KEY] = {
      generation: 0,
      inflight: new Map(),
      recentOk: new Map(),
      recentError: new Map(),
    };
  } else if (!g[FETCH_COALESCE_GLOBAL_KEY].recentError) {
    g[FETCH_COALESCE_GLOBAL_KEY].recentError = new Map();
  }
  g[FETCH_COALESCE_GLOBAL_KEY].generation ??= 0;
  return g[FETCH_COALESCE_GLOBAL_KEY]!;
}

/** Aynı GET için uçuş + kısa TTL tekil ağ (HMR / çoklu consumer / cache-bust params). */
const GET_COALESCE_MS = 4500;
const BOOTSTRAP_GET_COALESCE_MS = 30000;
const ERROR_GET_COALESCE_MS = 10000;

/** `/api/auth/me` burada yok — yalnızca tam pathname `/api/auth/me` birleştirilir (`/api/auth/me/language` hariç). */
const GET_COALESCE_PREFIXES = [
  '/api/notifications',
  '/api/settings/user',
  /** Uygulama listesi (localhost `/api/apps` veya köprü); çoklu tüketici aynı pencerede tek HTTP */
  '/api/apps',
  '/api/core/',
  '/api/users/me/companies',
  '/api/tasks',
  '/api/priorities',
  '/api/projects',
  '/api/translations',
  '/api/user-settings/',
  '/api/lookup/',
  '/api/activity-logs',
  '/api/dms/',
] as const;

const DEV_HIDDEN_TAB_DEFER_PREFIXES = [
  '/api/auth/me',
  '/api/settings/user',
  '/api/notifications',
  '/api/users/me/companies',
  '/api/apps',
  '/api/core/',
  '/api/user-settings/',
  '/api/lookup/',
  '/api/tasks',
  '/api/priorities',
  '/api/projects',
  '/api/translations',
  '/api/activity-logs',
] as const;

/** GET yanıtı pratikte tek kaynak; farklı timeout/config.params ile anahtar kırılmasın. */
const COALESCE_PARAMS_IGNORE_PATHS = new Set<string>([
  '/api/settings/user',
  '/api/auth/me',
  '/api/notifications/unread-count',
  '/api/notifications/preferences',
]);

function normalizeApiPath(url: string): string {
  const noQuery = url.split('?')[0];
  if (/^https?:\/\//i.test(noQuery)) {
    try {
      return new URL(noQuery).pathname;
    } catch {
      return noQuery;
    }
  }
  return noQuery;
}

/** Subpath deploy: `/core/api/...` ile `/api/...` aynı coalesce anahtarına düşsün. */
function stripViteBasePathFromPathname(pathname: string): string {
  const b = (import.meta.env.VITE_BASE_PATH || '').replace(/\/+$/, '');
  let p = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (b && p.startsWith(b)) {
    p = p.slice(b.length) || '/';
  }
  return p.startsWith('/') ? p : `/${p}`;
}

function canonicalPathForCoalesce(resolvedPathOrUrl: string): string {
  return stripViteBasePathFromPathname(normalizeApiPath(resolvedPathOrUrl));
}

function shouldCoalesceGet(pathname: string): boolean {
  const p = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (p === '/api/auth/me') return true;
  if (p.startsWith('/api/auth/')) return false;
  return GET_COALESCE_PREFIXES.some((pre) => p === pre || p.startsWith(pre));
}

function shouldDeferHiddenDevGet(pathname: string): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof document === 'undefined') return false;
  if (document.visibilityState !== 'hidden') return false;
  const p = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return DEV_HIDDEN_TAB_DEFER_PREFIXES.some((pre) => p === pre || p.startsWith(pre));
}

let devVisibleWait: Promise<void> | null = null;

function waitUntilDocumentVisibleForDev(): Promise<void> {
  if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
    return Promise.resolve();
  }
  if (devVisibleWait) return devVisibleWait;
  devVisibleWait = new Promise((resolve) => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') return;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      devVisibleWait = null;
      resolve();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
  });
  return devVisibleWait;
}

function stableParamsKey(params: unknown): string {
  if (params == null) return '';
  if (typeof params !== 'object' || Array.isArray(params)) {
    try {
      return JSON.stringify(params);
    } catch {
      return '';
    }
  }
  const o = params as Record<string, unknown>;
  const entries = Object.entries(o)
    .filter(([k]) => !['_', 'cb', 't', 'timestamp'].includes(k))
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(Object.fromEntries(entries));
}

function parseSearchToRecord(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!search) return out;
  const sp = new URLSearchParams(search);
  sp.forEach((v, k) => {
    out[k] = v;
  });
  return out;
}

/** Axios GET'te sayfa büyüklüğü vb. çoğu kez URL query'de; yalnızca config.params anahtarı yanlıştı (×N fırtına). */
function effectiveGetParamsForCoalesce(url: string, config?: AxiosRequestConfig): unknown {
  const qIdx = url.indexOf('?');
  const fromUrl = qIdx >= 0 ? parseSearchToRecord(url.slice(qIdx + 1)) : {};
  const fromCfg = config?.params;
  if (fromCfg == null) {
    return Object.keys(fromUrl).length ? fromUrl : undefined;
  }
  if (typeof fromCfg !== 'object' || Array.isArray(fromCfg)) {
    return Object.keys(fromUrl).length ? fromUrl : fromCfg;
  }
  return { ...fromUrl, ...(fromCfg as Record<string, unknown>) };
}

function companyScopeKeySuffix(canonicalApiPath: string, headers?: unknown): string {
  try {
    const explicit = readExplicitCompanyScopeHeader(headers);
    if (explicit) return `|co:${explicit}`;
    if (!pathRequiresSelectedCompanyOnRequest(canonicalApiPath)) return '';
    const id = resolveCompanyScopeIdForRequest(canonicalApiPath);
    return `|co:${id ?? 'none'}`;
  } catch {
    return '|co:none';
  }
}

function resolveInstanceUrl(instance: AxiosInstance, url: string): string {
  const noQuery = url.split('?')[0] || '';
  if (/^https?:\/\//i.test(noQuery)) {
    return noQuery;
  }
  const base = (instance.defaults?.baseURL as string | undefined) || '';
  if (!base) {
    return noQuery.startsWith('/') ? noQuery : `/${noQuery.replace(/^\//, '')}`;
  }
  try {
    const baseNorm = base.endsWith('/') ? base : `${base}/`;
    const rel = noQuery.replace(/^\//, '');
    return new URL(rel, baseNorm).toString().split('?')[0] || noQuery;
  } catch {
    return noQuery.startsWith('/') ? noQuery : `/${noQuery}`;
  }
}

function buildCoalesceKey(
  instance: AxiosInstance,
  url: string,
  config?: AxiosRequestConfig,
): string | null {
  const pathNoQuery = url.split('?')[0] || '';
  const resolved = resolveInstanceUrl(instance, pathNoQuery);
  const path = canonicalPathForCoalesce(resolved);
  if (!shouldCoalesceGet(path)) return null;
  const paramKey = COALESCE_PARAMS_IGNORE_PATHS.has(path)
    ? ''
    : stableParamsKey(effectiveGetParamsForCoalesce(url, config));
  return `${path}?${paramKey}${companyScopeKeySuffix(path, config?.headers)}`;
}

function buildCoalesceKeyFromRequestConfig(config: AxiosRequestConfig): string | null {
  const url = String(config.url || '');
  if (!url) return null;
  const baseURL = typeof config.baseURL === 'string' ? config.baseURL : '';
  const instanceLike = { defaults: { baseURL } } as AxiosInstance;
  return buildCoalesceKey(instanceLike, url, config);
}

function buildCoalesceKeyFromFetch(input: RequestInfo | URL, init?: RequestInit): string | null {
  const method = String(init?.method || (input instanceof Request ? input.method : 'get') || 'get').toLowerCase();
  if (method !== 'get') return null;

  const url = input instanceof Request ? input.url : String(input);
  if (!url) return null;
  const path = canonicalPathForCoalesce(url);
  if (!shouldCoalesceGet(path)) return null;
  const paramKey = COALESCE_PARAMS_IGNORE_PATHS.has(path)
    ? ''
    : stableParamsKey(effectiveGetParamsForCoalesce(url));
  return `${path}?${paramKey}${companyScopeKeySuffix(path, init?.headers ?? (input instanceof Request ? input.headers : undefined))}`;
}

function splitAxiosRequestArgs(
  urlOrConfig: string | AxiosRequestConfig,
  config?: AxiosRequestConfig,
): { method: string; url: string; config?: AxiosRequestConfig } | null {
  if (typeof urlOrConfig === 'string') {
    return {
      method: String(config?.method || 'get').toLowerCase(),
      url: urlOrConfig,
      config,
    };
  }

  if (!urlOrConfig || typeof urlOrConfig !== 'object') {
    return null;
  }

  return {
    method: String(urlOrConfig.method || 'get').toLowerCase(),
    url: String(urlOrConfig.url || ''),
    config: urlOrConfig,
  };
}

function pathFromCoalesceKey(key: string): string {
  return key.split('?')[0] || '';
}

function isNotificationPath(path: string): boolean {
  return path === '/api/notifications' || path.startsWith('/api/notifications/');
}

function coalesceGeneration(maps: CoalesceMaps | FetchCoalesceMaps, key: string): string {
  return `${maps.generation}:${isNotificationPath(pathFromCoalesceKey(key)) ? maps.notificationGeneration ?? 0 : 0}`;
}

/** Successful Notification writes invalidate only this shared service's reads. */
function invalidateNotificationGetCoalesceCache(): void {
  for (const maps of [getCoalesceMaps(), getFetchCoalesceMaps()]) {
    maps.notificationGeneration = (maps.notificationGeneration ?? 0) + 1;
    for (const cache of [maps.inflight, maps.recentOk, maps.recentError]) {
      for (const key of cache.keys()) if (isNotificationPath(pathFromCoalesceKey(key))) cache.delete(key);
    }
  }
}

function getErrorStatus(error: unknown): number | undefined {
  const maybeError = error as { response?: { status?: unknown }; status?: unknown } | null | undefined;
  const rawStatus = maybeError?.response?.status ?? maybeError?.status;
  const status = Number(rawStatus);
  return Number.isFinite(status) ? status : undefined;
}

function isAuthErrorStatus(status: number | undefined): boolean {
  return status === 401 || status === 403;
}

function successTtlForCoalesceKey(key: string): number {
  const path = pathFromCoalesceKey(key);
  if (
    path === '/api/auth/me' ||
    path === '/api/settings/user' ||
    path.startsWith('/api/user-settings/') ||
    path.startsWith('/api/lookup/') ||
    path === '/api/notifications/unread-count' ||
    path === '/api/notifications/summary' ||
    path === '/api/notifications/preferences' ||
    path === '/api/users/me/companies' ||
    path === '/api/apps' ||
    path.startsWith('/api/core/')
  ) {
    return BOOTSTRAP_GET_COALESCE_MS;
  }
  if (
    path === '/api/notifications' ||
    path === '/api/projects' ||
    path === '/api/tasks' ||
    path === '/api/priorities' ||
    path === '/api/activity-logs' ||
    path.startsWith('/api/activity-logs/') ||
    path.startsWith('/api/dms/')
  ) {
    return 12000;
  }
  return GET_COALESCE_MS;
}

function cloneAxiosResponse<T>(r: AxiosResponse<T>): AxiosResponse<T> {
  return {
    data: r.data,
    status: r.status,
    statusText: r.statusText,
    headers: r.headers,
    config: r.config,
    request: r.request,
  };
}

function runCoalescedAdapterGet(
  config: AxiosRequestConfig,
  rawAdapter: AxiosAdapter,
): Promise<AxiosResponse> {
  const maps = getCoalesceMaps();
  const { inflight, recentOk, recentError } = maps;
  const key = buildCoalesceKeyFromRequestConfig(config);
  if (!key) {
    return rawAdapter(config);
  }
  const generation = coalesceGeneration(maps, key);
  const now = Date.now();
  const ttlMs = successTtlForCoalesceKey(key);
  const hit = recentOk.get(key);
  if (hit && now - hit.at < ttlMs) {
    return Promise.resolve(cloneAxiosResponse(hit.response));
  }
  const errorHit = recentError.get(key);
  if (errorHit && now - errorHit.at < ERROR_GET_COALESCE_MS) {
    if (isAuthErrorStatus(getErrorStatus(errorHit.error))) {
      recentError.delete(key);
    } else {
      return Promise.reject(errorHit.error);
    }
  }
  const existing = inflight.get(key);
  if (existing) {
    return existing;
  }
  const send = async () => {
    const path = pathFromCoalesceKey(key);
    if (shouldDeferHiddenDevGet(path)) {
      await waitUntilDocumentVisibleForDev();
    }
    if (config.signal?.aborted) throw new axios.CanceledError();
    return rawAdapter(config);
  };
  const p = send()
    .then((res) => {
      if (coalesceGeneration(maps, key) === generation && !config.signal?.aborted) {
        recentOk.set(key, { at: Date.now(), response: res });
        recentError.delete(key);
      }
      return res;
    })
    .catch((error) => {
      if (coalesceGeneration(maps, key) !== generation || axios.isCancel(error) || config.signal?.aborted) {
        // A cancelled previous scope must not refill either cache.
      } else if (isAuthErrorStatus(getErrorStatus(error))) {
        recentError.delete(key);
      } else {
        recentError.set(key, { at: Date.now(), error });
      }
      throw error;
    })
    .finally(() => {
      if (inflight.get(key) === p) inflight.delete(key);
    });
  inflight.set(key, p);
  return p;
}

function resolveAxiosAdapter(): AxiosAdapter {
  const axiosAny = axios as unknown as {
    defaults: { adapter?: unknown };
    getAdapter?: (adapter: unknown) => AxiosAdapter;
  };
  const current = axiosAny.defaults.adapter;
  if (typeof current === 'function') {
    return current as AxiosAdapter;
  }
  if (typeof axiosAny.getAdapter === 'function') {
    return axiosAny.getAdapter(current || ['xhr', 'http', 'fetch']);
  }
  if (import.meta.env.MODE === 'test') {
    return async (config) => ({
      data: {},
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
      request: {},
    } as AxiosResponse);
  }
  throw new Error('Axios adapter bulunamadı');
}

const axiosDefaultsAny = axios.defaults as typeof axios.defaults & Record<symbol, boolean>;
if (!axiosDefaultsAny[AXIOS_ADAPTER_COALESCE_PATCHED]) {
  const rawAdapter = resolveAxiosAdapter();
  axios.defaults.adapter = async function axiosAdapterCoalesced(config) {
    attachSelectedCompanyIdToRequest(config);
    const scoped = isWorkspaceBoundRequest(String(config.url || '')) || pathRequiresSelectedCompanyOnRequest(String(config.url || '')) || Boolean(readExplicitCompanyScopeHeader(config.headers));
    const snapshot = scoped ? getCompanyScopeSnapshot() : null;
    if (snapshot) { assertWorkspaceRequestAllowed(snapshot); if (isWorkspaceBoundRequest(String(config.url || ''))) attachWorkspaceFence(config, snapshot); }
    const callerSignal = config.signal;
    if (snapshot) config.signal = combineCompanyScopeSignal(snapshot.signal, callerSignal as AbortSignal | undefined);
    const method = String(config.method || 'get').toLowerCase();
    try {
      if (snapshot) assertCompanyScopeCurrent(snapshot);
      const hasCallerCancellation = callerSignal && !automaticCompanySignals.has(callerSignal as AbortSignal);
      const response = await (method !== 'get' || hasCallerCancellation ? rawAdapter(config) : runCoalescedAdapterGet(config, rawAdapter));
      if (snapshot) assertCompanyScopeCurrent(snapshot);
      return response;
    } catch (error) {
      reportWorkspaceMismatch(error);
      if (snapshot) assertCompanyScopeCurrent(snapshot);
      throw error;
    }
  };
  axiosDefaultsAny[AXIOS_ADAPTER_COALESCE_PATCHED] = true;
}

const globalAny = globalThis as typeof globalThis & Record<symbol, boolean>;
if (typeof globalThis.fetch === 'function' && !globalAny[FETCH_GET_COALESCE_PATCHED]) {
  const rawFetch = globalThis.fetch.bind(globalThis);
  globalThis.fetch = async function qubeproFetchGetCoalesced(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    let scopedInit = withSelectedCompanyScopeFetchInit(input, init);
    const url = input instanceof Request ? input.url : String(input);
    const headers = scopedInit?.headers ?? (input instanceof Request ? input.headers : undefined);
    const scoped = isWorkspaceBoundRequest(url) || pathRequiresSelectedCompanyOnRequest(url) || Boolean(readExplicitCompanyScopeHeader(headers));
    const snapshot = scoped ? getCompanyScopeSnapshot() : null;
    if (snapshot) {
      assertWorkspaceRequestAllowed(snapshot);
      const fencedHeaders = new Headers(headers);
      if (isWorkspaceBoundRequest(url) && snapshot.membershipId && !fencedHeaders.has('X-Workspace-Membership')) fencedHeaders.set('X-Workspace-Membership', snapshot.membershipId);
      scopedInit = { ...scopedInit, headers: fencedHeaders };
    }
    const callerSignal = init?.signal ?? (input instanceof Request ? input.signal : undefined);
    const key = callerSignal ? null : buildCoalesceKeyFromFetch(input, scopedInit);
    const [metaInput, metaInit] = withDevFetchMetaHeaders(input, snapshot
      ? { ...scopedInit, signal: combineCompanyScopeSignal(snapshot.signal, callerSignal) } : scopedInit);

    const send = async () => {
      if (snapshot) assertCompanyScopeCurrent(snapshot);
      if (!key) return rawFetch(metaInput, metaInit);

      const maps = getFetchCoalesceMaps();
      const { inflight, recentOk, recentError } = maps;
      const generation = coalesceGeneration(maps, key);
      const now = Date.now();
      const ttlMs = successTtlForCoalesceKey(key);
      const hit = recentOk.get(key);
      if (hit && now - hit.at < ttlMs) {
        return hit.response.clone();
      }
      const errorHit = recentError.get(key);
      if (errorHit && now - errorHit.at < ERROR_GET_COALESCE_MS) {
        return Promise.reject(errorHit.error);
      }
      const existing = inflight.get(key);
      if (existing) {
        return existing.then((res) => res.clone());
      }

      const request = (async () => {
        const path = pathFromCoalesceKey(key);
        if (shouldDeferHiddenDevGet(path)) {
          await waitUntilDocumentVisibleForDev();
        }
        if (snapshot) assertCompanyScopeCurrent(snapshot);
        return rawFetch(metaInput, metaInit);
      })()
        .then((res) => {
          if (snapshot) assertCompanyScopeCurrent(snapshot);
          if (coalesceGeneration(maps, key) === generation && res.ok) {
            recentOk.set(key, { at: Date.now(), response: res.clone() });
            recentError.delete(key);
          }
          return res;
        })
        .catch((error) => {
          if (coalesceGeneration(maps, key) === generation && !metaInit?.signal?.aborted) recentError.set(key, { at: Date.now(), error });
          throw error;
        })
        .finally(() => {
          if (inflight.get(key) === request) inflight.delete(key);
        });
      inflight.set(key, request);
      return request.then((res) => res.clone());
    };
    try {
      const response = await send();
      if (response.status === 409 && response.headers.get('X-Workspace-Changed') === '1' && typeof window !== 'undefined') {
        window.dispatchEvent(new Event(WORKSPACE_CONTEXT_CHANGED_EVENT));
      }
      if (!snapshot) return response;
      assertCompanyScopeCurrent(snapshot);
      return guardCompanyScopeResponse(response, snapshot);
    } catch (error) {
      if (snapshot) assertCompanyScopeCurrent(snapshot);
      throw error;
    }
  };
  globalAny[FETCH_GET_COALESCE_PATCHED] = true;
}

void AXIOS_GET_COALESCE_PATCHED;
void AXIOS_REQUEST_COALESCE_PATCHED;
void splitAxiosRequestArgs;

export function clearAxiosGetCoalesceCache(): void {
  for (const maps of [getCoalesceMaps(), getFetchCoalesceMaps()]) {
    maps.generation += 1;
    maps.inflight.clear();
    maps.recentOk.clear();
    maps.recentError.clear();
  }
}

registerCompanyScopeInvalidator(clearAxiosGetCoalesceCache);

/** Capture ownership before async auth interceptors; an old draft must never be sent in the next company. */
function withCompanyScopeLifecycle<T extends AxiosInstance>(instance: T): T {
  const methods = new Map<PropertyKey, unknown>();
  const requestMethods = new Set(['request', 'get', 'delete', 'head', 'options', 'post', 'put', 'patch', 'postForm', 'putForm', 'patchForm']);
  const run = async (fn: Function, method: string, args: unknown[]) => {
    const requestStyle = method === 'request';
    const index = requestStyle ? (typeof args[0] === 'string' ? 1 : 0)
      : ['post', 'put', 'patch', 'postForm', 'putForm', 'patchForm'].includes(method) ? 2 : 1;
    const config = { ...(args[index] as AxiosRequestConfig | undefined) };
    const url = requestStyle && typeof args[0] !== 'string' ? config.url : args[0];
    const scoped = isWorkspaceBoundRequest(String(url || '')) || pathRequiresSelectedCompanyOnRequest(String(url || '')) || Boolean(readExplicitCompanyScopeHeader(config.headers));
    if (!scoped) return Reflect.apply(fn, instance, args);
    const snapshot = getCompanyScopeSnapshot();
    assertWorkspaceRequestAllowed(snapshot);
    if (isWorkspaceBoundRequest(String(url || ''))) attachWorkspaceFence(config, snapshot);
    const callerSignal = config.signal as AbortSignal | undefined;
    config.signal = combineCompanyScopeSignal(snapshot.signal, callerSignal);
    if (!callerSignal) automaticCompanySignals.add(config.signal as AbortSignal);
    const scopedArgs = [...args];
    scopedArgs[index] = config;
    try {
      assertCompanyScopeCurrent(snapshot);
      const response = await Reflect.apply(fn, instance, scopedArgs);
      assertCompanyScopeCurrent(snapshot);
      const actualMethod = String(config.method || method).replace(/Form$/u, '').toLowerCase();
      if (['post', 'put', 'patch', 'delete'].includes(actualMethod)
        && isNotificationPath(canonicalPathForCoalesce(String(url || '')))
        && response.status >= 200 && response.status < 300 && response.data?.success !== false) {
        invalidateNotificationGetCoalesceCache();
      }
      return response;
    } catch (error) {
      reportWorkspaceMismatch(error);
      assertCompanyScopeCurrent(snapshot);
      throw error;
    }
  };
  return new Proxy(instance, {
    apply(target, _thisArg, args) { return run(target, 'request', args); },
    get(target, key, receiver) {
      if (methods.has(key)) return methods.get(key);
      const value = Reflect.get(target, key, receiver);
      if (typeof value !== 'function') return value;
      if (key === 'create') {
        const create = (...args: unknown[]) => withCompanyScopeLifecycle(Reflect.apply(value, target, args) as AxiosInstance);
        methods.set(key, create);
        return create;
      }
      if (requestMethods.has(String(key))) {
        const method = (...args: unknown[]) => run(value, String(key), args);
        methods.set(key, method);
        return method;
      }
      return value;
    },
  });
}

export default withCompanyScopeLifecycle(axios);
